import { Color, Spacing } from '@persona-id/design-tokens';
import { useCallback, useMemo } from 'react';
import ReactPhoneInput, { PhoneInputProps as ReactPhoneInputProps } from 'react-phone-number-input';
import styled from 'styled-components';

import inputMixin from 'design-system/mixins/input';
import FormControlProps from 'lib/interfaces/FormControlProps';

import 'react-phone-number-input/style.css';

export interface PhoneInputProps extends FormControlProps, ReactPhoneInputProps {
  // Whether or not the autoFocus prop should be passed to the underlying input component.
  // Using `enableAutofocus` rather than `autoFocus` is intentional. If `autoFocus` is not
  // explicitly passed to an `input` component, it is stripped and becomes a no-op.
  //
  // WARN: `autoFocus` prop gets special polyfill treatment by React due to browser DOM incompatibilities.
  // For React to do this, the prop needs to be explicitly passed to the `input` component.
  // See: https://github.com/facebook/react/blob/67e3f3fb6e342f95f00215c84d5d013d7b0e1b33/packages/react-dom/src/client/ReactDOMComponent.js#L348-L351
  //
  // TODO: Move to FocusScope API if and only if the RFC passes:
  // https://github.com/reactjs/rfcs/issues/104.
  enableAutofocus?: boolean;
}

const isAndroid = (userAgent: string) => userAgent.includes('Android');

const ReactPhoneInputWrapper = ({
  hasError,
  hasWarning,
  enableAutofocus,
  onChange,
  ...otherProps
}: PhoneInputProps) => {
  // This component provides undefined if empty, which will not properly update
  // redux form state.
  const handleChange = useCallback(
    (value: string) => (value == null ? onChange('') : onChange(value)),
    [onChange],
  );

  // [PER-2284] We disable smart caret on Android because there is a bug that
  // resets the cursor position whenever formatting is done.
  // https://github.com/catamphetamine/react-phone-number-input/issues/75
  const smartCaret = useMemo(() => !isAndroid(navigator.userAgent), [navigator.userAgent]);
  // `autoFocus` prop below needs to be explicitly passed
  return (
    <ReactPhoneInput
      autoFocus={enableAutofocus}
      smartCaret={smartCaret}
      onChange={handleChange}
      {...otherProps}
      // Needed to avoid blur sending unformatted value. Without this, the
      // value will become invalid on blur when used in Redux-Form <Field/>.
      // https://github.com/catamphetamine/react-phone-number-input/issues/49#issuecomment-316990240
      onBlur={undefined}
    />
  );
};

const PhoneInput = styled(ReactPhoneInputWrapper)<PhoneInputProps>`
  ${inputMixin};
  /* Zeroing this out for larger click space */
  display: flex;
  /* When building for production, our own display rules overwrite the library's which is flex. Re-overwrite here. */
  padding: 0;

  & .PhoneInputCountry {
    padding-left: ${Spacing[4]};
    margin-right: 0;
  }

  & .PhoneInputInput {
    ${inputMixin};

    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
    /* -2 for border. This component's border exists outside the input, unlike our regular input. */
    height: ${({ theme }) => parseFloat(theme.element.height.md) - 2}px;
    padding-left: ${Spacing[2]};
  }

  & .PhoneInputCountrySelectArrow {
    border-color: ${Color.CoreSlate200};
    border-style: solid;
    border-width: 0 0 2px 2px;
    color: transparent;
    content: '';
    display: block;
    height: 8px;
    margin: -4px 0 0 8px;
    opacity: 1;
    transform: rotate(-45deg);
    transition:
      transform 0.1s,
      border-color 0.2s ease;
    width: 8px;
  }

  &:focus-within .PhoneInputCountrySelectArrow {
    border-color: ${({ theme }) => theme.color.primary.brand};
  }
`;

export default PhoneInput;
